import React, { useEffect, useRef, useState } from "react";
import { colors, fontMono, s } from "../lib/theme";

const BAN_OPTIONS = [
  { label: "30 min", minutes: 30 },
  { label: "1 h", minutes: 60 },
  { label: "2 h", minutes: 120 },
];

export default function ChatPanel({ socket, activityId, initialChat, myUserId, containerStyle }) {
  const [messages, setMessages] = useState(initialChat?.history || []);
  const [onlineUsers, setOnlineUsers] = useState(initialChat?.onlineNow || []);
  const [mutedUntil, setMutedUntil] = useState(initialChat?.mutedUntil || null);
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const [banTarget, setBanTarget] = useState(null); // userId elegido para banear
  const listRef = useRef(null);

  // No vienen del isOrganizer general de la página — son específicos del
  // chat: un superadmin puede leer cualquier sala para depurar sin
  // molestar, pero no escribe ni modera salvo que de verdad pertenezca al
  // club/actividad.
  const canChat = !!initialChat?.canChat;
  const canModerate = !!initialChat?.canModerate;

  useEffect(() => {
    if (!socket) return;
    const onMessage = (msg) => {
      setMessages((prev) => [...prev, msg]);
      if (msg.type === "connect" && msg.callsign) {
        setOnlineUsers((prev) => (prev.some((u) => u.callsign === msg.callsign) ? prev : [...prev, { callsign: msg.callsign, name: msg.name }]));
      }
      if (msg.type === "disconnect" && msg.callsign) {
        setOnlineUsers((prev) => prev.filter((u) => u.callsign !== msg.callsign));
      }
      if (msg.type === "ban" && msg.bannedUserId === myUserId) {
        setMutedUntil(msg.mutedUntil);
      }
    };
    socket.on("chat_new_message", onMessage);
    return () => socket.off("chat_new_message", onMessage);
  }, [socket, myUserId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages]);

  const isBanned = mutedUntil && new Date(mutedUntil) > new Date();
  const canWriteNow = canChat && !isBanned;

  const send = (e) => {
    e.preventDefault();
    if (!text.trim() || !socket) return;
    setError(null);
    socket.emit("chat_message", { activityId, text: text.trim() }, (res) => {
      if (res?.error) {
        setError(res.error);
        if (res.mutedUntil) setMutedUntil(res.mutedUntil);
      } else {
        setText("");
      }
    });
  };

  const banUser = (userId, minutes) => {
    if (!socket) return;
    socket.emit("chat_ban", { activityId, userId, minutes }, (res) => {
      if (res?.error) setError(res.error);
      setBanTarget(null);
    });
  };

  return (
    <div style={{
      width: 300, minWidth: 300, display: "flex", flexDirection: "column",
      border: `1px solid ${colors.line}`, borderRadius: 10, background: colors.panel, height: 640,
      ...containerStyle,
    }}>
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${colors.lineStrong}` }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: colors.text }}>Chat de la actividad</div>
        <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 2 }}>
          {onlineUsers.length} conectado{onlineUsers.length === 1 ? "" : "s"}: {onlineUsers.map((u) => u.callsign).join(", ") || "—"}
        </div>
        {!canChat && (
          <div style={{ fontSize: 10.5, color: colors.textFaint, marginTop: 4 }}>
            Solo lectura — no perteneces al grupo de esta actividad.
          </div>
        )}
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
        {messages.length === 0 && <p style={{ fontSize: 12, color: colors.textFaint }}>Todavía no hay mensajes.</p>}
        {messages.map((m, i) => {
          const prevDate = i > 0 ? new Date(messages[i - 1].created_at).toISOString().slice(0, 10) : null;
          const thisDate = new Date(m.created_at).toISOString().slice(0, 10);
          return (
            <React.Fragment key={m.id}>
              {thisDate !== prevDate && (
                <div style={{ textAlign: "center", fontSize: 10.5, color: colors.textFaint, margin: "6px 0" }}>
                  — {thisDate} (UTC) —
                </div>
              )}
              <ChatLine m={m} canModerate={canModerate} myUserId={myUserId} onBanClick={() => setBanTarget(m.user_id)} />
            </React.Fragment>
          );
        })}
      </div>

      {canModerate && banTarget && (
        <div style={{ padding: "8px 12px", borderTop: `1px solid ${colors.lineStrong}`, background: colors.panelRaised }}>
          <div style={{ fontSize: 11.5, color: colors.textDim, marginBottom: 6 }}>Banear (mutear) durante:</div>
          <div style={{ display: "flex", gap: 6 }}>
            {BAN_OPTIONS.map((opt) => (
              <button key={opt.minutes} onClick={() => banUser(banTarget, opt.minutes)} style={{ ...s.btnGhost, fontSize: 11, padding: "5px 9px" }}>
                {opt.label}
              </button>
            ))}
            <button onClick={() => setBanTarget(null)} style={{ background: "transparent", border: "none", color: colors.textFaint, fontSize: 11, cursor: "pointer" }}>
              cancelar
            </button>
          </div>
        </div>
      )}

      {error && <div style={{ fontSize: 11, color: "#ffb4b4", padding: "4px 12px" }}>{error}</div>}

      {canChat ? (
        <>
          <form onSubmit={send} style={{ display: "flex", gap: 6, padding: "10px 12px", borderTop: `1px solid ${colors.lineStrong}` }}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={isBanned ? "Baneado temporalmente" : "Escribe un mensaje…"}
              disabled={!canWriteNow}
              style={{ ...s.input, flex: 1, opacity: canWriteNow ? 1 : 0.5, fontSize: 13 }}
            />
            <button type="submit" disabled={!canWriteNow || !text.trim()} style={{ ...s.btnPrimary, opacity: !canWriteNow || !text.trim() ? 0.4 : 1 }}>
              Enviar
            </button>
          </form>
          {isBanned && (
            <p style={{ fontSize: 10.5, color: "#ffb4b4", padding: "0 12px 8px" }}>
              No puedes escribir hasta las {new Date(mutedUntil).toISOString().slice(11, 16)} UTC.
            </p>
          )}
        </>
      ) : (
        <div style={{ padding: "10px 12px", borderTop: `1px solid ${colors.lineStrong}`, fontSize: 11.5, color: colors.textFaint }}>
          Solo puedes leer este chat.
        </div>
      )}
    </div>
  );
}

function formatChatTime(isoString) {
  return new Date(isoString).toISOString().slice(11, 16); // HH:MM UTC
}

function ChatLine({ m, canModerate, myUserId, onBanClick }) {
  if (m.type === "connect") {
    return <div style={{ fontSize: 11.5, color: colors.green, fontStyle: "italic" }}>{m.body}</div>;
  }
  if (m.type === "disconnect") {
    return <div style={{ fontSize: 11.5, color: "#ff8a8a", fontStyle: "italic" }}>{m.body}</div>;
  }
  if (m.type === "ban") {
    return (
      <div style={{ fontSize: 11.5, color: colors.amber, fontStyle: "italic" }}>
        {m.body} <span style={{ fontSize: 10, opacity: 0.7 }}>· {formatChatTime(m.created_at)}</span>
      </div>
    );
  }

  const canBan = canModerate && m.user_id && m.user_id !== myUserId;

  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
      <span style={{ fontFamily: fontMono, fontWeight: 700, color: colors.amber }}>{m.callsign}: </span>
      <span style={{ color: colors.text }}>{renderWithMentions(m.body)}</span>
      <span style={{ fontSize: 10, color: colors.textFaint, marginLeft: 6 }}>{formatChatTime(m.created_at)}</span>
      {canBan && (
        <button onClick={onBanClick} title="Banear" style={{ marginLeft: 6, background: "transparent", border: "none", color: colors.textFaint, fontSize: 10, cursor: "pointer" }}>
          ⛔
        </button>
      )}
    </div>
  );
}

function renderWithMentions(text) {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) =>
    part.startsWith("@") ? (
      <span key={i} style={{ color: colors.amber, fontWeight: 700 }}>{part}</span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}
